import mapboxgl, { type LayerSpecification, type SourceSpecification } from 'mapbox-gl';
import { type RefObject, useEffect, useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import type { FollowedVehicleData } from '../../../store/selectors/selectFollowedFlightData';

const DRONE_MODEL_URI = '/models/drone.glb';
const DRONE_WITH_PACKAGE_MODEL_URI = '/models/drone-with-package.glb';
const DRONE_MODEL_SOURCE_ID = 'followed-vehicle-model-source';
const DRONE_MODEL_LAYER_ID = 'followed-vehicle-model-layer';
const DRONE_MODEL_ID = 'followed-vehicle-drone';
const DRONE_DEBUG_SCALE = 8;
const AUTO_FOLLOW_FEATURE_ENABLED = import.meta.env.VITE_ENABLE_AUTO_FOLLOW_FEATURE === 'true';

type CameraTunableValue = {
  min: number;
  max: number;
  step: number;
  default: number;
};

const CAMERA_HEIGHT_METERS: CameraTunableValue = {
  min: 4,
  max: 20,
  step: 1,
  default: 8
};

const CAMERA_FOLLOW_DISTANCE_METERS: CameraTunableValue = {
  min: 4,
  max: 40,
  step: 2,
  default: 12
};

const MODEL_YAW_OFFSET_DEGREES = 225;
// Mapbox/glTF axis conventions require a fixed base rotation before applying telemetry deltas.
const MODEL_BASE_ORIENTATION = [90, 0, 0] as const;

type FollowedVehicleFlightData = {
  location: {
    lng: number;
    lat: number;
    alt: number;
  };
  attitude: {
    roll: number;
    pitch: number;
    yaw: number;
  };
};

const metersToLat = (meters: number) => (meters / 6378137) * (180 / Math.PI);

const metersToLng = (meters: number, latitude: number) => {
  const latitudeRadians = (latitude * Math.PI) / 180;
  return (meters / (6378137 * Math.cos(latitudeRadians))) * (180 / Math.PI);
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const buildDroneModelsSpec = (flightData: FollowedVehicleFlightData, hasPackage: boolean) => ({
  [DRONE_MODEL_ID]: {
    uri: hasPackage ? DRONE_WITH_PACKAGE_MODEL_URI : DRONE_MODEL_URI,
    position: [flightData.location.lng, flightData.location.lat],
    // Apply telemetry attitude directly on top of the fixed model frame alignment.
    orientation: [
      MODEL_BASE_ORIENTATION[0] + flightData.attitude.roll,
      MODEL_BASE_ORIENTATION[1] + flightData.attitude.pitch,
      flightData.attitude.yaw + MODEL_YAW_OFFSET_DEGREES
    ],
    scale: [DRONE_DEBUG_SCALE, DRONE_DEBUG_SCALE, DRONE_DEBUG_SCALE]
  }
});

export const useFollowedVehicleMapEffects = ({
  mapRef,
  followedVehicleData
}: {
  mapRef: RefObject<MapRef | null>;
  followedVehicleData: FollowedVehicleData | null;
}) => {
  const followedVehicleFlightData = followedVehicleData?.flightData ?? null;
  const followedVehicleHasPackage = followedVehicleData?.hasPackage ?? null;
  const [cameraHeightMeters, setCameraHeightMeters] = useState(CAMERA_HEIGHT_METERS.default);
  const [cameraFollowDistanceMeters, setCameraFollowDistanceMeters] = useState(CAMERA_FOLLOW_DISTANCE_METERS.default);

  useEffect(() => {
    if (!AUTO_FOLLOW_FEATURE_ENABLED) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowUp' ? -1 : 1;

      setCameraHeightMeters((previousValue) =>
        clamp(previousValue + direction * CAMERA_HEIGHT_METERS.step, CAMERA_HEIGHT_METERS.min, CAMERA_HEIGHT_METERS.max)
      );

      setCameraFollowDistanceMeters((previousValue) =>
        clamp(
          previousValue + direction * CAMERA_FOLLOW_DISTANCE_METERS.step,
          CAMERA_FOLLOW_DISTANCE_METERS.min,
          CAMERA_FOLLOW_DISTANCE_METERS.max
        )
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();

    if (!AUTO_FOLLOW_FEATURE_ENABLED) {
      if (map.getLayer(DRONE_MODEL_LAYER_ID)) {
        map.removeLayer(DRONE_MODEL_LAYER_ID);
      }
      if (map.getSource(DRONE_MODEL_SOURCE_ID)) {
        map.removeSource(DRONE_MODEL_SOURCE_ID);
      }
      return;
    }

    const upsertDroneModel = () => {
      const currentSource = map.getSource(DRONE_MODEL_SOURCE_ID) as
        | (mapboxgl.Source & { setModels?: (models: Record<string, unknown>) => void })
        | undefined;

      const modelsSpec =
        followedVehicleFlightData && followedVehicleHasPackage !== null
          ? buildDroneModelsSpec(followedVehicleFlightData, followedVehicleHasPackage)
          : {};

      if (!currentSource) {
        map.addSource(DRONE_MODEL_SOURCE_ID, {
          type: 'model',
          models: modelsSpec
        } as SourceSpecification);
      } else {
        currentSource.setModels?.(modelsSpec);
      }

      const zElevation = Math.max(followedVehicleFlightData?.location.alt ?? 0, 0);

      if (!map.getLayer(DRONE_MODEL_LAYER_ID)) {
        map.addLayer({
          id: DRONE_MODEL_LAYER_ID,
          type: 'model',
          source: DRONE_MODEL_SOURCE_ID,
          paint: {
            'model-opacity': 1,
            // Per Mapbox docs: use layer translation for altitude above terrain.
            'model-translation': [0, 0, zElevation],
            'model-type': 'location-indicator'
          }
        } as LayerSpecification);
      } else {
        map.setPaintProperty(DRONE_MODEL_LAYER_ID, 'model-translation', [0, 0, zElevation]);
      }

      // Keep model above route/style layers that might be added after style reload.
      map.moveLayer(DRONE_MODEL_LAYER_ID);
    };

    if (map.isStyleLoaded()) {
      upsertDroneModel();
    }

    map.on('style.load', upsertDroneModel);
    return () => {
      map.off('style.load', upsertDroneModel);
    };
  }, [followedVehicleFlightData, followedVehicleHasPackage, mapRef]);

  useEffect(() => {
    if (!AUTO_FOLLOW_FEATURE_ENABLED || !followedVehicleFlightData || !mapRef.current) {
      return;
    }

    const map = mapRef.current.getMap();
    const { lng, lat, alt } = followedVehicleFlightData.location;
    const yaw = followedVehicleFlightData.attitude.yaw;
    const targetAltitude = Math.max(alt, 0);

    const yawRadians = (yaw * Math.PI) / 180;
    const offsetEastMeters = -Math.sin(yawRadians) * cameraFollowDistanceMeters;
    const offsetNorthMeters = -Math.cos(yawRadians) * cameraFollowDistanceMeters;
    const cameraLng = lng + metersToLng(offsetEastMeters, lat);
    const cameraLat = lat + metersToLat(offsetNorthMeters);

    const camera = map.getFreeCameraOptions();
    camera.position = mapboxgl.MercatorCoordinate.fromLngLat(
      { lng: cameraLng, lat: cameraLat },
      targetAltitude + cameraHeightMeters
    );
    camera.lookAtPoint({ lng, lat }, [0, 0, 1], targetAltitude);
    map.setFreeCameraOptions(camera);
  }, [cameraFollowDistanceMeters, cameraHeightMeters, followedVehicleFlightData, mapRef]);
};
